import classNames from "classnames";
import React from "react";
import { useComponentState } from "../../utils/customHook.util";
import Button from "../Common/Buttons";
import { Flex } from "../Common/Flex";
import Label from "../Common/Label";
import ModalOverlay from "../Common/Overlay/ModalOverlay";
import GoogleImageSelector from "./GooglePlace/GoogleImageSelector";
import ImageCrawler from "./ImageCrawler";
import "./ImageSearch.css";

type ImageSource = "google" | "website";
type ImageSelection = { source: ImageSource; url: string };

type ImageSearchProps = {
    googlePlaceId: string | undefined;
    websiteUrl: string | undefined;
    onClose: () => void;
    onSave: (images: string[] | undefined) => void;
};

type ImageSearchState = {
    source: ImageSource;
    selections: ImageSelection[];
};

function ImageSearch(props: ImageSearchProps) {
    const { googlePlaceId, websiteUrl, onClose, onSave } = props;
    const { state, setState } = useComponentState<ImageSearchState>({
        source: googlePlaceId ? "google" : "website",
        selections: [],
    });

    function arrangeSelections(source: ImageSource, urls: string[]) {
        const googleSelections =
            source === "google"
                ? urls.map<ImageSelection>((url) => ({ url, source: "google" }))
                : state.selections.filter((it) => it.source === "google");
        const websiteSelections =
            source === "website"
                ? urls.map<ImageSelection>((url) => ({
                      url,
                      source: "website",
                  }))
                : state.selections.filter((it) => it.source === "website");

        setState({
            selections: [...googleSelections, ...websiteSelections],
        });
    }

    return (
        <ModalOverlay.Base
            title={
                <span>
                    <Flex.Row gap="large" alignItems="center" justifyContent="center">
                        <Button.Ghost
                            disabled={!googlePlaceId}
                            onClick={() => {
                                setState({
                                    source: "google",
                                });
                            }}
                        >
                            {state.source === "google" ? (
                                <Label.Medium.SemiBold.N10>
                                    Google
                                </Label.Medium.SemiBold.N10>
                            ) : (
                                <Label.Medium.SemiBold.N60>
                                    Google
                                </Label.Medium.SemiBold.N60>
                            )}
                        </Button.Ghost>

                        <Button.Ghost
                            disabled={!websiteUrl}
                            onClick={() => {
                                setState({
                                    source: "website",
                                });
                            }}
                        >
                            {state.source === "website" ? (
                                <Label.Medium.SemiBold.N10>
                                    Website
                                </Label.Medium.SemiBold.N10>
                            ) : (
                                <Label.Medium.SemiBold.N60>
                                    Website
                                </Label.Medium.SemiBold.N60>
                            )}
                        </Button.Ghost>
                    </Flex.Row>
                </span>
            }
        >
            <ModalOverlay.Content>
                {googlePlaceId && (
                    <Flex.Column
                        className={classNames("image-search__images", {
                            "image-search__images--hidden": state.source !== "google",
                        })}
                    >
                        <GoogleImageSelector
                            googlePlaceId={googlePlaceId}
                            initialValues={state.selections
                                .filter((it) => it.source === "google")
                                .map((it) => it.url)}
                            onSelect={(newUrls) => {
                                arrangeSelections("google", newUrls);
                            }}
                        />
                    </Flex.Column>
                )}

                {websiteUrl && (
                    <Flex.Column
                        className={classNames("image-search__images", {
                            "image-search__images--hidden": state.source !== "website",
                        })}
                    >
                        <ImageCrawler
                            baseUrl={websiteUrl}
                            initialValues={state.selections
                                .filter((it) => it.source === "website")
                                .map((it) => it.url)}
                            onSelect={(newUrls) => {
                                arrangeSelections("website", newUrls);
                            }}
                        />
                    </Flex.Column>
                )}
            </ModalOverlay.Content>

            <ModalOverlay.Footer>
                <Flex.Row justifyContent="end" gap="medium">
                    <Button
                        label="Cancel"
                        onClick={() => {
                            onClose();
                        }}
                    />
                    <Button.N10
                        label={`Add selected (${state.selections.length})`}
                        onClick={() => {
                            onSave(state.selections.map((it) => it.url));
                        }}
                    />
                </Flex.Row>
            </ModalOverlay.Footer>
        </ModalOverlay.Base>
    );
}

export default ImageSearch;
